/* eslint-disable padding-line-between-statements */
import { BreakpointTrackerContext } from '@jutro/layout';
import { useLocation, useHistory } from 'react-router-dom';
import { useAuthentication } from '@xengage/gw-digital-auth-react';
import { useValidation } from '@xengage/gw-portals-validation-react';
import { ViewModelForm, ViewModelServiceContext } from '@xengage/gw-portals-viewmodel-react';
import { WizardPage, wizardProps } from '@xengage/gw-portals-wizard-react';
import { LoadSaveService } from 'twia-capability-quoteandbind-residential';
import _  from 'lodash';
import React, { useCallback, useContext, useState, useEffect } from 'react';
import { Loader, useModal } from '@jutro/components';
import { useTranslator } from '@jutro/locale';
import { format } from 'date-fns';
import metadata from './DiscountsPage.metadata.json5';
import messages from './DiscountsPage.messages.js';
import styles from './DiscountsPage.module.scss';
import TFPAQuoteUtilsService from '../../../../capabilities/tfpa-capability-quoteandbind-residential/services/TFPAQuoteUtilsService.js';
import ErrorPopupUtil from 'ent-components-platform-react/util/ErrorPopupUtil.js';

const LOB_PATH = {
    PersonalAuto: 'lobData.personalAuto.preQualQuestionSets.children[0].value',
    Homeowners: 'lobData.homeowners.preQualQuestionSets.children[0].value',
    HOPHomeowners: 'lobData.homeowners.preQualQuestionSets.children[0].value',
    BusinessOwners:
        'lobData.businessOwners.preQualQuestionSets.children[0].value',
    BusinessAuto:
        'lobData.businessOwners.preQualQuestionSets.children[0].value',
};

function TFPADiscountsPage(props) {
    const breakpoint = useContext(BreakpointTrackerContext);
    const { wizardData: submissionVM, updateWizardData } = props;
    const { authHeader } = useAuthentication();
    const viewModelService = useContext(ViewModelServiceContext);
    const translator = useTranslator();
    const [isLoading, setIsLoading] = useState(false);
    const { showAlert, showConfirm } = useModal();
    const [invalidComponent, setInvalidComponents] = useState([]);
    const [showErrors, setShowErrors] = useState(false);
    const [priorLosses, setPriorLosses] = useState([]);
    const [showPriorSection, setShowPriorSection] = useState(false);
    const location = useLocation();
    const history = useHistory();
    const [homeSecurityDeviceValue, setHomeSecurityDeviceValue] = useState(
        _.get(submissionVM.value, 'lobData.homeowners_tfpa.coverables.rating.homeSecurityDeviceCredit_Ext')
    );
    const {
        isComponentValid,
        initialValidation,
        onValidate,
    } = useValidation('TFPADiscountsPage');

    const policyTypeCode = submissionVM.lobData.homeowners_tfpa.policyType_TWIA.value.code;

    const {quoteID} = location?.state?.quoteentry;

    const formatAmount = (amount) => `$${parseFloat(amount).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

    const formatChargeable = (chargeable) => chargeable ? 'Yes' : 'No';

    //US26834
    const fetchPriorLossesData = useCallback(() => {
        setIsLoading(true);
        TFPAQuoteUtilsService.loadPriorLosses(submissionVM.value, authHeader)
            .then(pL => {
                const formattedData = pL.map(item => ({
                    ...item,
                    totalPayment: {
                        ...item.totalPayment,
                        amount: formatAmount(item.totalPayment.amount),
                    },
                    lossDate: format(new Date(item.lossDate), 'MM/dd/yyyy'),
                    chargeable: formatChargeable(item.chargeable),
                }));
                setPriorLosses(formattedData);
                setShowPriorSection(true);
                 _.set(submissionVM,'lobData.homeowners_tfpa.coverables.rating.priorLosses_Ext.value', pL);
                 _.set(submissionVM, 'lobData.homeowners_tfpa.coverables.yourHome.primaryAddress.mustStandardize_Ext', false);
                updateWizardData(submissionVM);
            })
            .catch(error => {       //DE16328
                showAlert({
                    title: 'Error',
                    message: translator(messages.unableToRetrieve),
                    status: 'error',
                    icon: 'mi-error-outline',
                    confirmButtonText: translator(messages.close),
                })
                .then(results => {
                    if (results === 'close') {
                        return _.noop();
                    }
                    const redirectPath = `/quotes/${quoteID}/summary`
                    history.push(redirectPath)
                }, _.noop)
            })
            .finally(() => {
                setIsLoading(false);
            });
    }, [submissionVM, authHeader, formatAmount, formatChargeable, updateWizardData, showAlert]);

    //US26834
    const writeValue = useCallback(
        (value, path) => {
            _.set(submissionVM, path , value);
            updateWizardData(submissionVM);
            setHomeSecurityDeviceValue(value);
            if (policyTypeCode !== 'DP2') { 
                setShowPriorSection(true);
                fetchPriorLossesData();
            }
        }, [submissionVM, policyTypeCode, fetchPriorLossesData, updateWizardData, priorLosses]
    );
    
    useEffect(() => {
        if( _.get(submissionVM, 'lobData.homeowners_tfpa.coverables.rating.homeSecurityDeviceCredit_Ext.value') === undefined){
            _.set(submissionVM.value, 'lobData.homeowners_tfpa.coverables.rating', {}); //US26834
            updateWizardData(submissionVM);
        }  
        if (policyTypeCode === 'DP2') {
            fetchPriorLossesData(); 
            setShowPriorSection(true);
        }
        if(!_.isUndefined(homeSecurityDeviceValue)) {
            fetchPriorLossesData();
            _.set(submissionVM,'lobData.homeowners_tfpa.coverables.rating.homeSecurityDeviceCredit_Ext.value', homeSecurityDeviceValue);
            updateWizardData(submissionVM);
        }
    }, [submissionVM]);

    const onNext = useCallback(async () => {
        
        _.set(submissionVM, 'lobData.homeowners_tfpa.coverables.yourHome.primaryAddress.isOverridden_Ext',true);
        _.set(submissionVM, 'lobData.homeowners_tfpa.coverables.yourHome.primaryAddress.mustStandardize_Ext', false);
        updateWizardData(submissionVM);
        if (!isComponentValid) {
            setShowErrors(true);
            return false;
        }
        setShowErrors(false);
    
        try {
            // DE16292
            const {yourHome} = submissionVM.lobData.homeowners_tfpa.coverables;

            yourHome.performValuation_Ext = true;

            const updatedSubmission = await LoadSaveService.updateDraftSubmission(submissionVM.value, authHeader);
            
            yourHome.performValuation_Ext = false;
            submissionVM.value = updatedSubmission;
    
            updateWizardData(submissionVM);
    
            const { validationIssues } = submissionVM.value.errorsAndWarnings;
            // if (validationIssues && validationIssues.issues && validationIssues.issues.length > 0) {
            //     setShowErrorDescription(validationIssues.issues[0].reason);
            //     return false;
            // }
    
            return submissionVM;
        } catch (error) {
            // DE18521 - Skipping TFPA permission validation for Migrated Submission
            const errorMessage = _.split(error.baseError, 'ErrorMessage: ')[1];
            if (errorMessage.includes("Context user does not have permission to modify/create valuation")) {
                updateWizardData(submissionVM);
                return submissionVM;
            }
            console.error('Error updating draft submission:', error);
            ErrorPopupUtil.showSaveErrorPopup(showAlert, false);

            return false;
        }
    }, [isComponentValid, submissionVM, authHeader, updateWizardData, showAlert]);
    

    const findInvalidComponent = useCallback((isValid, componentID) => {
        setShowErrors(false);
        
        if (isValid === false) {
            setInvalidComponents((oldValue) => [...oldValue, componentID]);
        }

        if (isValid === true) {
            setInvalidComponents((oldValue) => oldValue.filter((val) => val !== componentID));
        }
    }, []);

    const overrideProps = {
        '@field': {
            // apply to all fields
            labelPosition: breakpoint === 'desktop' ? 'left' : 'top',
        },
        questionSets: {
            path: LOB_PATH['Homeowners'],
        },
        AdditionalInterests: {
            submissionVM, updateWizardData, showErrors, findInvalidComponent, onValidate
        },
        AdditionalInsureds: {
            submissionVM, updateWizardData, showErrors, findInvalidComponent, onValidate
        },
        additionalInterestsAccordion: {
            errorState: _.includes(invalidComponent, 'AdditionalInterestDetails') && showErrors
        },
        additionalInsuredsAccordion: {
            errorState: _.includes(invalidComponent, 'AdditionalInsuredDetails') && showErrors
        },
        homeSecurityDevice: {
            visible: policyTypeCode !== 'DP2',
            value: homeSecurityDeviceValue,
            showRequired: true,
            showErrors
        },
        explanationSection: {
            visible: policyTypeCode !== 'DP2',
        },
        homeSecurityContent: {
            content: translator(messages.homeSecurityExplanation)
        },
        automaticSprinklerContent: {
            content: translator(messages.automaticSprinklerExplanation)
        },
        noPriorLoss: {
            visible: _.isEmpty(priorLosses) && showPriorSection,
        },
        priorLosses: {
            visible: !_.isEmpty(priorLosses) && showPriorSection,
        },
        priorLossesListTable: {
            data: priorLosses
        }
        
    };

    const resolvers = {
        resolveClassNameMap: styles
    };

    return (
      
        <WizardPage
            onNext={onNext}
            skipWhen={initialValidation}
        >
            <Loader loaded={!isLoading}>
                <ViewModelForm
                    uiProps={metadata.pageContent}
                    model={submissionVM}
                    overrideProps={overrideProps}
                    onModelChange={updateWizardData}
                    onValueChange={writeValue}
                    showErrors={showErrors}
                    classNameMap={resolvers.resolveClassNameMap}
                    onValidationChange={onValidate}
                />
            </Loader>
        </WizardPage>
    );
}

TFPADiscountsPage.propTypes = wizardProps;
export default TFPADiscountsPage;
